import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { MessageSquarePlus, Minus, Plus, ShoppingCart, SlidersHorizontal, StickyNote } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Category, Product } from '@/types'
import { ITEM_NOTE_MAX, ORDER_NOTE_MAX, type PricedCartItem } from '../cartLine'

export interface UnsentItemsSectionProps {
  /**
   * Staged lines with their resolved unit price already attached. The rail
   * must never re-derive a price from `product.price` — with modifiers folded
   * into the line, that figure is the pre-modifier base and would disagree
   * with both the footer total and the bill.
   */
  cart: PricedCartItem[]
  categoryById: Map<string, Category>
  categoryColor: (cat: Category | undefined, fallback: string) => string
  formatCurrency: (n: number) => string
  /** When continuing an existing order, label becomes "Staging / New items" */
  continuing: boolean
  onIncrement: (product: Product) => void
  /** Targets one LINE — two lines of the same product decrement separately. */
  onDecrement: (lineId: string) => void
  onItemNotesChange: (lineId: string, note: string) => void
  /**
   * Open the options sheet for a staged line (F-MOD-N7). This is how OPTIONAL
   * groups are reached: tapping the tile does not prompt for them, because
   * that would put a second tap on every latte all shift.
   */
  onEditOptions?: (lineId: string) => void
  /** Whether a product has any option groups at all, required or not. */
  lineHasOptions?: (productId: string) => boolean
  flashLineId: string | null
  /** Keyed by line id. */
  cartRowRefs: React.MutableRefObject<Record<string, HTMLTableRowElement | null>>
  notes: string
  onNotesChange: (value: string) => void
  liveAnnouncementId: number
  liveAnnouncementText: string
}

/**
 * Notes popover for a single cart line. Local draft state so the operator can
 * cancel without polluting the cart; commits on Save.
 */
function ItemNotesPopover({
  productName,
  value,
  onChange,
}: {
  productName: string
  value: string
  onChange: (v: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  const hasNote = value.trim().length > 0

  const handleSave = () => {
    onChange(draft.trim())
    setOpen(false)
  }

  const handleClear = () => {
    onChange('')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors',
                'hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                hasNote && 'text-primary'
              )}
              aria-label={hasNote ? `Edit note for ${productName}` : `Add note to ${productName}`}
            >
              {hasNote ? (
                <StickyNote className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <MessageSquarePlus className="h-3.5 w-3.5" aria-hidden />
              )}
              {hasNote && (
                <span
                  className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary"
                  aria-hidden
                />
              )}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{hasNote ? 'Edit note' : 'Add note'}</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[min(320px,calc(100vw-2rem))] p-3"
      >
        <Label className="text-xs text-muted-foreground">Note for {productName}</Label>
        <Textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, ITEM_NOTE_MAX))}
          placeholder="e.g. No onions, extra spicy"
          rows={3}
          className="mt-1.5 text-sm"
        />
        <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
          <span>Sent to kitchen with this item</span>
          <span className="tabular-nums">
            {draft.length} / {ITEM_NOTE_MAX}
          </span>
        </div>
        <div className="mt-2 flex justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground"
            onClick={handleClear}
            disabled={!hasNote && draft.trim() === ''}
          >
            Clear
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Compact "Order note" trigger for the cart heading — mirrors the per-item
 * popover but binds to the order-level notes field. Replaces the always-on
 * Notes input that previously sat below the cart.
 */
function OrderNotePopover({
  value,
  onChange,
  totalNoteCount,
}: {
  value: string
  onChange: (v: string) => void
  totalNoteCount?: number
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  const hasNote = value.trim().length > 0
  const count = totalNoteCount ?? (hasNote ? 1 : 0)

  const handleSave = () => {
    onChange(draft)
    setOpen(false)
  }

  const handleClear = () => {
    onChange('')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'relative inline-flex h-8 items-center gap-1.5 rounded-md border border-border/70 bg-background px-2.5 text-xs font-medium text-muted-foreground transition-colors',
                'hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                count > 0 && 'border-primary/30 text-foreground'
              )}
              aria-label={count > 0 ? 'Edit notes' : 'Add order note'}
            >
              <StickyNote className="h-3.5 w-3.5" aria-hidden />
              <span>{count > 0 ? `Notes (${count})` : 'Add note'}</span>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">
          {count > 0 ? `${count} note${count === 1 ? '' : 's'} attached` : 'Add a note for this order'}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[min(340px,calc(100vw-2rem))] p-3"
      >
        <Label className="text-xs text-muted-foreground">Order note</Label>
        <Textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, ORDER_NOTE_MAX))}
          placeholder="Anything the kitchen or staff should know about this whole order"
          rows={4}
          className="mt-1.5 text-sm"
        />
        <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
          <span>Saved with the order</span>
          <span className="tabular-nums">
            {draft.length} / {ORDER_NOTE_MAX}
          </span>
        </div>
        <div className="mt-2 flex justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground"
            onClick={handleClear}
            disabled={!hasNote && draft.trim() === ''}
          >
            Clear
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" className="h-8 text-xs" onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function UnsentItemsSection({
  cart,
  categoryById,
  categoryColor,
  formatCurrency,
  continuing,
  onIncrement,
  onDecrement,
  onItemNotesChange,
  onEditOptions,
  lineHasOptions,
  flashLineId,
  cartRowRefs,
  notes,
  onNotesChange,
  liveAnnouncementId,
  liveAnnouncementText,
}: UnsentItemsSectionProps) {
  return (
    <TooltipProvider delayDuration={250}>
      <section aria-labelledby="unsent-items-heading">
        <span key={liveAnnouncementId} className="sr-only" aria-live="polite" aria-atomic="true">
          {liveAnnouncementText}
        </span>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {continuing ? 'Staging (not sent)' : 'Current cart'}
            </div>
            <h3
              id="unsent-items-heading"
              className="flex items-center gap-2 text-base font-semibold tracking-tight"
            >
              <ShoppingCart className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
              {continuing ? 'New items' : 'Cart'} ({cart.length})
            </h3>
          </div>
          {cart.length > 0 && (() => {
            const itemsWithNotes = cart.filter(
              (it) => (it.special_instructions ?? '').trim().length > 0
            ).length
            const orderNotePresent = (notes ?? '').trim().length > 0
            const totalNoteCount = itemsWithNotes + (orderNotePresent ? 1 : 0)
            return (
              <OrderNotePopover
                value={notes}
                onChange={onNotesChange}
                totalNoteCount={totalNoteCount}
              />
            )
          })()}
        </div>
        {cart.length === 0 ? (
          <p className="mt-1 text-muted-foreground text-sm">Cart is empty</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[268px] table-fixed border-collapse text-sm">
              <colgroup>
                <col style={{ width: '36%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '22%' }} />
              </colgroup>
              <thead>
                <tr className="border-b border-border bg-muted/70">
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Item
                  </th>
                  <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Rate
                  </th>
                  <th className="px-1 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Qty
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {cart.map((item) => {
                  const lineTotal = item.unitPrice * item.quantity
                  const showCartFlash = flashLineId === item.lineId
                  const lineCat = item.product.category_id
                    ? categoryById.get(item.product.category_id)
                    : undefined
                  const categoryAccent = categoryColor(lineCat, item.product.name)
                  const note = item.special_instructions ?? ''
                  return (
                    <tr
                      key={item.lineId}
                      ref={(el) => {
                        cartRowRefs.current[item.lineId] = el
                      }}
                      className={cn(
                        'border-b border-border last:border-b-0 border-l-[3px] transition-[box-shadow,filter]',
                        'hover:brightness-[0.985] dark:hover:brightness-[1.04]',
                        showCartFlash &&
                          'ring-1 ring-inset ring-primary/30 animate-pulse [@media(prefers-reduced-motion:reduce)]:animate-none'
                      )}
                      style={{
                        borderLeftColor: categoryAccent,
                        backgroundColor: `color-mix(in srgb, ${categoryAccent} 13%, hsl(var(--card)))`,
                      }}
                    >
                      <td className="min-w-0 px-3 py-2.5 align-middle">
                        <div className="flex items-end gap-1.5">
                          <span className="line-clamp-2 min-w-0 font-medium leading-snug text-foreground">
                            {item.product.name}
                          </span>
                          <ItemNotesPopover
                            productName={item.product.name}
                            value={note}
                            onChange={(v) => onItemNotesChange(item.lineId, v)}
                          />
                          {onEditOptions && lineHasOptions?.(item.product.id) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => onEditOptions(item.lineId)}
                                  aria-label={
                                    item.selections.length > 0
                                      ? `Change options for ${item.product.name}`
                                      : `Add options to ${item.product.name}`
                                  }
                                  className={cn(
                                    'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border/70 text-muted-foreground transition-colors',
                                    'hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                    item.selections.length > 0 && 'border-primary/40 bg-primary/5 text-foreground',
                                  )}
                                >
                                  <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                {item.selections.length > 0 ? 'Change options' : 'Add options'}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        {/* Chosen options, under the line they belong to —
                            the whole point of the feature. */}
                        {item.modifierLabels && item.modifierLabels.length > 0 && (
                          <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                            {item.modifierLabels.join(' · ')}
                          </span>
                        )}
                        {note && (
                          <span
                            className="mt-0.5 block truncate text-[11px] italic text-muted-foreground"
                            title={note}
                          >
                            “{note}”
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 align-middle text-right text-sm tabular-nums text-muted-foreground">
                        {formatCurrency(item.unitPrice)}
                      </td>
                      <td className="px-1 py-2 align-middle">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="h-9 w-9 shrink-0 touch-manipulation"
                            onClick={() => onDecrement(item.lineId)}
                            aria-label={`Decrease ${item.product.name}`}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="min-w-[1.5rem] text-center text-sm font-semibold tabular-nums">
                            {item.quantity}
                          </span>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="h-9 w-9 shrink-0 touch-manipulation"
                            onClick={() => onIncrement(item.product)}
                            aria-label={`Increase ${item.product.name}`}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 align-middle text-right text-sm font-semibold tabular-nums">
                        {formatCurrency(lineTotal)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </TooltipProvider>
  )
}
